import React from 'react';
import { Row, Col, Button, Alert, Spinner } from 'react-bootstrap';
import { OrderCartItem, ContactInfo } from './OrderTypes';
import { Link } from 'react-router-dom';
import { formatDesignFileName } from '../AppNavbar';

interface SecureCheckoutStepProps {
  cartItems: OrderCartItem[];
  cartTotal: number;
  contactInfo: ContactInfo;
  errors: Record<string, string>;
  submitError: string;
  orderSubmitting: boolean;
  creatingRzpOrder: boolean;
  verifyingPayment: boolean;
  onContactChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onDeliveryTypeChange: (deliveryType: 'pickup' | 'home') => void;
  onRemoveFromCart: (id: string) => void;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onBack: () => void;
  onCheckout: () => void;
}

const SecureCheckoutStep: React.FC<SecureCheckoutStepProps> = ({
  cartItems,
  cartTotal,
  contactInfo,
  errors,
  submitError,
  orderSubmitting,
  creatingRzpOrder,
  verifyingPayment,
  onContactChange,
  onDeliveryTypeChange,
  onRemoveFromCart,
  onUpdateQuantity,
  onBack,
  onCheckout,
}) => (
  <div className="step-content-fade text-start">
    <h4 className="fw-bold text-dark mb-4">Secure Checkout</h4>

    <div className="mb-5 border rounded-4 p-4 bg-light">
      <h5 className="fw-bold text-dark mb-3">Review Cart Items</h5>
      {cartItems.length === 0 ? (
        <div className="text-center py-4 text-muted">
          <p>No items in cart. Please configure products first.</p>
          <Link to="/order" className="btn btn-primary-custom px-4" onClick={onBack}>Go Back</Link>
        </div>
      ) : (
        <>
          {cartItems.map(item => (
            <div key={item.id} className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-3">
              <div>
                <h6 className="fw-bold text-dark mb-1">{item.productName}</h6>
                <div className="text-muted small" style={{ fontSize: '11px' }}>
                  {Object.entries(item.options).map(([k, v]) => (
                    <span key={k} className="me-2 text-capitalize">{k.replace(/([A-Z])/g, ' $1')}: <strong>{v}</strong></span>
                  ))}
                  <div className="mt-2 d-flex align-items-center gap-2">
                    <span className="fw-semibold text-dark me-1" style={{ fontSize: '12px' }}>Qty:</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary py-1 px-2 border-light bg-white rounded-3 d-flex align-items-center justify-content-center"
                      onClick={() => onUpdateQuantity(item.id, Math.max(1, item.quantity - 1))}
                      style={{ width: '28px', height: '28px' }}
                    >
                      <i className="fas fa-minus" style={{ fontSize: '10px' }}></i>
                    </button>
                    <input
                      type="number"
                      className="form-control form-control-sm text-center py-1 border-light shadow-sm"
                      value={item.quantity || ''}
                      onChange={(e) => onUpdateQuantity(item.id, Math.max(1, Number(e.target.value)))}
                      style={{ borderRadius: '6px', fontSize: '13px', fontWeight: 'bold', width: '60px', height: '28px', padding: '0 4px' }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary py-1 px-2 border-light bg-white rounded-3 d-flex align-items-center justify-content-center"
                      onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                      style={{ width: '28px', height: '28px' }}
                    >
                      <i className="fas fa-plus" style={{ fontSize: '10px' }}></i>
                    </button>
                  </div>
                  {item.designFileName && <div className="mt-1 text-primary" title={formatDesignFileName(item.designFileName)}><i className="far fa-file-image me-1"></i>{formatDesignFileName(item.designFileName)}</div>}
                </div>
              </div>
              <div className="text-end">
                <div className="fw-bold text-primary">₹{item.estimatedPrice.toLocaleString('en-IN')}</div>
                <button className="btn btn-sm btn-link text-danger p-0 mt-1" onClick={() => onRemoveFromCart(item.id)}>Remove</button>
              </div>
            </div>
          ))}
          <div className="d-flex justify-content-between align-items-center pt-2">
            <span className="fw-bold text-dark">Subtotal Total:</span>
            <span className="fw-bold text-primary h5 mb-0">₹{cartTotal.toLocaleString('en-IN')}</span>
          </div>
        </>
      )}
    </div>

    <div className="row g-3">
      <Col md={12}>
        <h5 className="fw-bold text-dark mb-3 mt-2 border-bottom pb-2">Delivery & Customer Information</h5>
      </Col>

      <Col md={6}>
        <div className="mb-3">
          <label className="form-label text-dark fw-bold" style={{ fontSize: '13px' }}>Your Name*</label>
          <input
            type="text"
            name="name"
            className={`form-input py-2 ${errors.name ? 'border-danger' : ''}`}
            placeholder="Full Name"
            value={contactInfo.name}
            onChange={onContactChange}
          />
          {errors.name && <div className="text-danger mt-1" style={{ fontSize: '11px' }}>{errors.name}</div>}
        </div>
      </Col>

      <Col md={6}>
        <div className="mb-3">
          <label className="form-label text-dark fw-bold" style={{ fontSize: '13px' }}>WhatsApp / Phone Number*</label>
          <input
            type="tel"
            name="phone"
            className={`form-input py-2 ${errors.phone ? 'border-danger' : ''}`}
            placeholder="10-digit Mobile Number"
            value={contactInfo.phone}
            onChange={onContactChange}
          />
          {errors.phone && <div className="text-danger mt-1" style={{ fontSize: '11px' }}>{errors.phone}</div>}
        </div>
      </Col>

      <Col md={12}>
        <div className="mb-3">
          <label className="form-label text-dark fw-bold" style={{ fontSize: '13px' }}>Email Address*</label>
          <input
            type="email"
            name="email"
            className={`form-input py-2 ${errors.email ? 'border-danger' : ''}`}
            placeholder="name@example.com"
            value={contactInfo.email}
            onChange={onContactChange}
          />
          {errors.email && <div className="text-danger mt-1" style={{ fontSize: '11px' }}>{errors.email}</div>}
        </div>
      </Col>

      <Col md={12}>
        <div className="mb-3">
          <label className="form-label text-dark fw-bold" style={{ fontSize: '13px' }}>Delivery Options</label>
          <div className="d-flex gap-3">
            <button
              type="button"
              className={`btn py-2 px-3 flex-fill border transition ${contactInfo.deliveryType === 'pickup' ? 'btn-primary border-primary fw-semibold' : 'btn-outline-light bg-white border-light text-muted'}`}
              onClick={() => onDeliveryTypeChange('pickup')}
              style={{ borderRadius: '10px', fontSize: '13px' }}
            >
              <i className="fas fa-store me-2"></i> Self Store Pickup
            </button>
            <button
              type="button"
              className={`btn py-2 px-3 flex-fill border transition ${contactInfo.deliveryType === 'home' ? 'btn-primary border-primary fw-semibold' : 'btn-outline-light bg-white border-light text-muted'}`}
              onClick={() => onDeliveryTypeChange('home')}
              style={{ borderRadius: '10px', fontSize: '13px' }}
            >
              <i className="fas fa-shipping-fast me-2"></i> Home Delivery
            </button>
          </div>
        </div>
      </Col>

      {contactInfo.deliveryType === 'home' && (
        <Col md={12}>
          <div className="mb-3">
            <label className="form-label text-dark fw-bold" style={{ fontSize: '13px' }}>Shipping Address*</label>
            <textarea
              name="address"
              className={`form-input ${errors.address ? 'border-danger' : ''}`}
              placeholder="Full Street Address, Landmark, Pincode"
              style={{ height: '70px' }}
              value={contactInfo.address}
              onChange={onContactChange}
            />
            {errors.address && <div className="text-danger mt-1" style={{ fontSize: '11px' }}>{errors.address}</div>}
          </div>
        </Col>
      )}

      <Col md={12}>
        <div className="mb-3">
          <label className="form-label text-dark fw-bold" style={{ fontSize: '13px' }}>Special Instructions / Notes</label>
          <textarea
            name="notes"
            className="form-input"
            placeholder="Any special print requests, bleed details, or custom delivery timings..."
            style={{ height: '80px' }}
            value={contactInfo.notes}
            onChange={onContactChange}
          />
        </div>
      </Col>
    </div>

    <div className="mt-4 pt-3 border-top d-flex flex-column gap-3">
      {submitError && (
        <Alert variant="danger" className="border-0 rounded-3 mb-0" style={{ fontSize: '13px' }}>
          ⚠️ {submitError}
        </Alert>
      )}
      <div className="d-flex flex-column flex-sm-row justify-content-between gap-3">
        <Button variant="outline-secondary" className="py-2.5" onClick={onBack}>
          <i className="fas fa-arrow-left me-1"></i> Add More Products
        </Button>
        <Button
          variant="primary"
          className="py-2.5 d-flex align-items-center justify-content-center gap-2"
          onClick={onCheckout}
          disabled={orderSubmitting || creatingRzpOrder || verifyingPayment || cartItems.length === 0}
        >
          {orderSubmitting || creatingRzpOrder || verifyingPayment ? (
            <><Spinner size="sm" animation="border" /> Processing Checkout...</>
          ) : (
            <>Pay & Confirm Order (₹{cartTotal.toLocaleString('en-IN')}) <i className="fas fa-credit-card ms-1"></i></>
          )}
        </Button>
      </div>
    </div>
  </div>
);

export default SecureCheckoutStep;
